import { Box, Flex, Text, Tab, Tabs, TabList, TabPanels, TabPanel } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  FetchDimensionGroup,
  FetchDimensionProperties,
  getBuilderDimensions,
  getDimensionProperty,
  getSelectedDimension,
  getBaseScenario,
  getDimensionLoaderStatus,
  getLoaderStatus,
  handlePropertySection,
  UpdateDimensionProperties,
  handleLoader,
} from 'redux/BuilderModeSlice';
import InputField from 'components/InputField';
import FigmaSettingsSlidersIcon from 'components/Icons/FigmaSettingsSlidersIcon';
import FigmaInfoIcon from 'components/Icons/FigmaInfoIcon';

import { DimensionsPropertiesSkeleton } from './DimensionsSkeletons';
import {
  ROBOTO,
  ROBOTO_MEDIUM,
  fieldLabelStyle,
  fieldInputStyle,
  PanelSelect,
  PanelShell,
} from './PanelUi';
import { neutral, radius, text as textToken } from './designTokens';

const BuilderPropertiesComponent = () => {
  const dimensionProperty = useSelector(getDimensionProperty);
  const builderDimension = useSelector(getBuilderDimensions);
  const selectedDimension: any = useSelector(getSelectedDimension);
  const selectedScenario = useSelector(getBaseScenario);
  const dimensionLoader = useSelector(getDimensionLoaderStatus);
  const loaderStatus = useSelector(getLoaderStatus);

  const dispatch: any = useDispatch();
  const handleClose = () => dispatch(handlePropertySection());

  // Up to 3 segments comfortably share the 310px track; beyond that they'd
  // truncate to initials, so the track scrolls with natural-width segments.
  const isEvenSegments = (dimensionProperty?.length || 0) <= 3;

  const handlePropertiesChange = async (
    value: string,
    id: number,
    type: string,
    dataFormat: string,
  ) => {
    dispatch(handleLoader(true));
    // dispatch(handlePropertySection());
    await (type === 'type' && dataFormat
      ? dispatch(
          UpdateDimensionProperties({
            property_id: id,
            request_body: {
              [type]: typeof value === 'boolean' ? value : `${value}`,
            },
          }),
        ).then(() => {
          dispatch(
            UpdateDimensionProperties({
              property_id: id,
              request_body: {
                data_format: JSON.stringify({ dimension: null }),
              },
            }),
          );
        })
      : dispatch(
          UpdateDimensionProperties({
            property_id: id,
            request_body: {
              [type]: typeof value === 'boolean' ? value : `${value}`,
            },
          }),
        ).then(() => {
          if (type === 'data_format' || (type === 'type' && value === 'dimension')) {
            dispatch(
              FetchDimensionProperties({
                selectedId: selectedDimension?.id,
                parameters: { scenario_id: selectedScenario?.id },
              }),
            ).then((response_: any) => {
              if (response_?.payload?.properties) {
                response_?.payload?.properties?.map((data: any) => {
                  if (data?.data_format) {
                    dispatch(
                      FetchDimensionGroup({
                        data,
                        parameters: { scenario_id: selectedScenario?.id },
                      }),
                    );
                  }
                  return null;
                });
              }
            });
          }
        }));
    dispatch(handleLoader(false));
  };
  return (
    <PanelShell
      icon={<FigmaSettingsSlidersIcon width={15} height={15} color={textToken.secondary} />}
      title='Properties'
      onClose={handleClose}
    >
      <>
        {dimensionLoader || loaderStatus ? (
          <DimensionsPropertiesSkeleton />
        ) : dimensionProperty?.length ? (
          // NOT variant='unstyled' — this app's tabs theme overrides that
          // variant with real styling (violet left/right borders on every
          // tab, which rendered as curved brackets against our rounded
          // segments). 'secondary' is the one genuinely empty variant.
          <Tabs isLazy variant='secondary' display='flex' flexDirection='column' flex='1' minH={0}>
            {/* Segmented control: one grey track with the active segment as a
                raised white chip. Property names are user-defined and can be
                long, so the track scrolls horizontally rather than squeezing
                every segment into an unreadable sliver. */}
            <Box
              px='12px'
              pt='12px'
              pb='12px'
              flexShrink={0}
              borderBottom={`1px solid ${neutral.borderSubtle}`}
            >
              <TabList
                border='none !important'
                borderWidth={0}
                display='flex'
                flexWrap='nowrap'
                gap='2px'
                bg='#F1F1F4'
                borderRadius={radius.control}
                p='4px'
                overflowX='auto'
                overflowY='hidden'
                sx={{
                  // The theme's `size: main` forces `flex: 1` + `h: 10` on every
                  // Tab. With only a few properties the even split is what we
                  // want; past that it crushes them into unreadable slivers, so
                  // switch to intrinsic width + a scrolling track. Borders are
                  // zeroed here too so no theme variant can reintroduce them.
                  '& > button': {
                    border: 'none !important',
                    height: 'auto !important',
                    minHeight: '0 !important',
                    ...(isEvenSegments
                      ? { flex: '1 1 0 !important', minWidth: '0 !important' }
                      : { flex: '0 0 auto !important', width: 'auto !important' }),
                  },
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {dimensionProperty?.map((data: any, index: number) => (
                  <Tab
                    key={index}
                    maxW={isEvenSegments ? undefined : '130px'}
                    whiteSpace='nowrap'
                    overflow='hidden'
                    textOverflow='ellipsis'
                    fontSize='12px'
                    fontWeight='500'
                    fontFamily={ROBOTO_MEDIUM}
                    color={textToken.secondary}
                    bg='transparent'
                    border='none'
                    borderRadius={radius.control}
                    px='12px'
                    py='8px'
                    transition='background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease'
                    _hover={{ color: textToken.primary }}
                    _selected={{
                      bg: 'white',
                      color: textToken.primary,
                      boxShadow: 'none',
                    }}
                  >
                    {data?.name}
                  </Tab>
                ))}
              </TabList>
            </Box>
            <TabPanels flex='1' minH={0} overflowY='auto' className='scroll'>
              {dimensionProperty?.map((data: any, index: number) => {
                return (
                  // Padding lives on the inner Flex, not TabPanel — the Tabs
                  // theme overrides TabPanel's own padding prop.
                  <TabPanel key={index} p={0}>
                    <Flex flexDir='column' gap='12px' px='12px' py='12px'>
                      <Box>
                        <Text {...fieldLabelStyle}>Help</Text>
                        <InputField
                          defaultValue={data?.help}
                          onKeyDown={(event: any) => {
                            if (event?.keyCode === 13 && event.target.value.trim().length > 0)
                              handlePropertiesChange(
                                event?.target?.value,
                                data?.id,
                                'help',
                                data?.data_format,
                              );
                          }}
                          placeholder='Enter description here'
                          {...fieldInputStyle}
                        />
                      </Box>
                      <Box>
                        <Text {...fieldLabelStyle}>Type</Text>
                        <PanelSelect
                          value={data?.type}
                          placeholder='Select type'
                          options={[
                            { value: 'number', label: 'Number' },
                            { value: 'dimension', label: 'Dimension' },
                            { value: 'text', label: 'Text' },
                          ]}
                          onChange={(next) =>
                            handlePropertiesChange(next, data?.id, 'type', data?.data_format)
                          }
                        />
                      </Box>
                      {(data?.type === 'dimension' || data?.type === 'Dimension') && (
                        <Box>
                          <Text {...fieldLabelStyle}>Connected Dimension</Text>
                          <PanelSelect
                            value={JSON.parse(data?.data_format)?.dimension}
                            placeholder='Select data format'
                            options={(builderDimension || []).map((data_: any) => ({
                              value: String(data_?.id),
                              label: data_?.name,
                            }))}
                            onChange={(next) =>
                              handlePropertiesChange(
                                JSON.stringify({ dimension: next }),
                                data?.id,
                                'data_format',
                                data?.data_format,
                              )
                            }
                          />
                        </Box>
                      )}
                      <Box>
                        <Text {...fieldLabelStyle}>Default Value</Text>
                        <InputField
                          defaultValue={data?.default_value}
                          onKeyDown={(event: any) => {
                            if (event?.keyCode === 13 && event.target.value.trim().length > 0)
                              handlePropertiesChange(
                                event?.target?.value,
                                data?.id,
                                'default_value',
                                data?.data_format,
                              );
                          }}
                          placeholder='Enter default value'
                          {...fieldInputStyle}
                        />
                      </Box>
                      {/* The text fields only commit on Enter (existing
                          behaviour) — surfacing it so edits aren't silently lost. */}
                      <Flex
                        alignItems='center'
                        gap='8px'
                        bg='#F9F9FB'
                        border={`1px solid ${neutral.borderSubtle}`}
                        borderRadius={radius.control}
                        px='12px'
                        py='8px'
                        mt='2px'
                      >
                        <FigmaInfoIcon width={13} height={13} color={textToken.muted} />
                        <Text
                          fontSize='12px'
                          fontFamily={ROBOTO}
                          color={textToken.secondary}
                          lineHeight='1.4'
                        >
                          Press Enter to save text changes.
                        </Text>
                      </Flex>
                    </Flex>
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        ) : (
          <Flex flex='1' alignItems='center' justifyContent='center' px='12px'>
            <Text fontSize='12px' fontFamily={ROBOTO} color={textToken.muted} textAlign='center'>
              No properties yet for this dimension.
            </Text>
          </Flex>
        )}
      </>
    </PanelShell>
  );
};
export default BuilderPropertiesComponent;
